import { DeepGramStt } from "../classes/DeepGramStt";
import { RecordingObjectType } from "../enums";
import { createContext, useContext, useMemo, useRef, useState } from "react";
import { MicRecorder } from "../utils/mic-recorder";
import { useTimer } from "use-timer";
import { apiRequest } from "../api";
import { useAccount } from "./use-account";
import { useDatadog } from "./use-datadog";
import { NoSoundWarningBanner } from "../components/NoSoundWarningBanner";
import { useToast } from "../hooks/use-toast";

export enum MicRecorderState {
  IDLE,
  RECORDING,
  PAUSED_RECORDING,
  INITIALIZING,
  PAUSING,
  PROCESSING_AUDIO
}

interface IAudioDevice {
  label: string;
  deviceId: string;
}

export const useMicRecorderProvider = () => {
  const {
    time,
    start: startTimer,
    pause: pauseTimer,
    reset: resetTimer
  } = useTimer();

  const { trackEvent, trackError } = useDatadog();

  const { failureToast, dismissToasts } = useToast();

  const [volume, setVolume] = useState(0);
  const [soundDetected, setSoundDetected] = useState(false);
  const [recordingObjectType, setRecordingObjectType] =
    useState<RecordingObjectType>(null);
  const [objectId, setObjectId] = useState<string>(null);
  const [liveTranscript, setLiveTranscript] = useState<string>("");
  const { companyName } = useAccount();

  const onProcessRef =
    useRef<(transcript: string, objectId: string) => Promise<void>>(null);

  const [micRecorderState, setMicRecorderState] = useState<MicRecorderState>(
    MicRecorderState.IDLE
  );

  const [defaultAudioDevice, setDefaultAudioDevice] =
    useState<IAudioDevice>(null);

  const deepGramStt = useRef<DeepGramStt>(new DeepGramStt());
  const micRecorder = useRef(new MicRecorder());

  const transcriptRef = useRef<string>("");

  const formattedTime = useMemo(() => {
    const minutes = Math.floor(time / 60);
    const secs = time % 60;
    return `${minutes}:${secs.toString().padStart(2, "0")}`;
  }, [time]);

  const startRecording = async (
    objectType: RecordingObjectType,
    onProcess: (transcript: string, objectId: string) => Promise<void>
  ) => {
    setMicRecorderState(MicRecorderState.INITIALIZING);
    await deepGramStt.current.init(
      (transcript: string) => setLiveTranscript(transcript),
      [companyName, "Vojomo"],
      trackEvent,
      trackError
    );

    setRecordingObjectType(objectType);

    let audioDevice = defaultAudioDevice;

    if (micRecorderState === MicRecorderState.PAUSED_RECORDING) {
      trackEvent("resuming_recording", {
        defaultAudioDevice,
        objectType,
        objectId
      });
      audioDevice = await micRecorder.current.resume(
        deepGramStt.current.getSocket()
      );
    } else if (micRecorderState === MicRecorderState.IDLE) {
      trackEvent("started_recording", {
        objectId,
        objectType,
        defaultAudioDevice
      });

      const data = await apiRequest({
        method: "POST",
        subRoute: "audio",
        path: "/create_object",
        body: {
          objectType
        }
      });

      setObjectId(data["objectId"]);
      transcriptRef.current = "";
      setLiveTranscript("");
      audioDevice = await micRecorder.current.start(
        deepGramStt.current.getSocket(),
        setSoundDetected,
        setVolume
      );
    } else {
      //throw error
    }

    startTimer();

    setDefaultAudioDevice(audioDevice);
    setMicRecorderState(MicRecorderState.RECORDING);
    onProcessRef.current = onProcess;
  };

  const pauseRecording = async () => {
    trackEvent("paused_recording", {
      time: formattedTime,
      defaultAudioDevice,
      objectId,
      recordingObjectType
    });

    setMicRecorderState(MicRecorderState.PAUSING);
    await micRecorder.current.pause();
    pauseTimer();
    let transcript = deepGramStt.current.end();

    transcriptRef.current += transcript + "\n";
    setMicRecorderState(MicRecorderState.PAUSED_RECORDING);

    return transcriptRef.current;
  };

  const stopRecording = async () => {
    trackEvent("stopped_recording", {
      time: formattedTime,
      defaultAudioDevice,
      objectId,
      recordingObjectType
    });

    const { transcript: deepGramTranscript, requestId } =
      deepGramStt.current.end();

    transcriptRef.current = transcriptRef.current.trim() + deepGramTranscript;

    setMicRecorderState(MicRecorderState.PROCESSING_AUDIO);

    await micRecorder.current.stop();

    if (requestId) {
      apiRequest({
        subRoute: "audio",
        path: "/track_audio_usage",
        method: "POST",
        body: {
          requestId,
          refId: objectId,
          refType: recordingObjectType,
          text: transcriptRef.current
        }
      }).catch((e) => {
        trackError("error_track_audio_usage", e, {
          requestId,
          refId: objectId,
          refType: recordingObjectType,
          text: transcriptRef.current,
          defaultAudioDevice
        });
      });
    }

    if (!transcriptRef.current) {
      const file = await micRecorder.current.getFile();

      const formData = new FormData();
      formData.append("file", file, file.name);

      trackEvent("transcribing_file", {
        fileSize: file.size,
        defaultAudioDevice,
        recordingObjectType,
        objectId
      });

      try {
        transcriptRef.current = await apiRequest<string>({
          subRoute: "audio",
          path: `/transcribe_file?ref_type=${recordingObjectType}&ref_id=${objectId}`,
          method: "POST",
          formData
        });
      } catch (e) {
        trackError("error_transcribe_file", e, {
          requestId,
          refId: objectId,
          refType: recordingObjectType,
          text: transcriptRef.current,
          defaultAudioDevice
        });
      }
    }

    if (transcriptRef.current.trim() === "") {
      trackError(
        "error_empty_transcript",
        {},
        {
          requestId,
          refId: objectId,
          refType: recordingObjectType,
          text: transcriptRef.current,
          defaultAudioDevice
        }
      );

      failureToast(
        "We couldn't get any transcript from your audio, try again please...",
        {
          position: "top-center"
        }
      );
      setTimeout(dismissToasts, 3000);
    } else {
      await onProcessRef.current(transcriptRef.current, objectId);
    }

    resetTimer();

    setRecordingObjectType(null);
    setMicRecorderState(MicRecorderState.IDLE);
    setLiveTranscript("");
  };

  const isRecording = useMemo(() => {
    return micRecorderState === MicRecorderState.RECORDING;
  }, [micRecorderState]);

  const isInitializing = useMemo(() => {
    return micRecorderState === MicRecorderState.INITIALIZING;
  }, [micRecorderState]);

  const isPausedRecording = useMemo(() => {
    return micRecorderState === MicRecorderState.PAUSED_RECORDING;
  }, [micRecorderState]);

  const isProcessingAudio = useMemo(() => {
    return micRecorderState === MicRecorderState.PROCESSING_AUDIO;
  }, [micRecorderState]);

  const isPausing = useMemo(() => {
    return micRecorderState === MicRecorderState.PAUSING;
  }, [micRecorderState]);

  const isBusy = useMemo(() => {
    return micRecorderState !== MicRecorderState.IDLE;
  }, [micRecorderState]);

  return {
    startRecording,
    stopRecording,
    pauseRecording,

    recordingObjectType,

    isBusy,

    isPausing,
    isProcessingAudio,

    isRecording,
    isPausedRecording,
    isInitializing,

    micRecorderState,
    defaultAudioDevice,
    soundDetected,

    time,
    formattedTime,
    liveTranscript,

    volume
  };
};

export const MicRecorderProvider = ({ children }) => {
  const micRecorderProvider = useMicRecorderProvider();

  return (
    <MicRecorderContext.Provider value={micRecorderProvider}>
      <NoSoundWarningBanner />
      {children}
    </MicRecorderContext.Provider>
  );
};

export type MicRecorderProps = ReturnType<typeof useMicRecorderProvider>;
export const MicRecorderContext = createContext<MicRecorderProps>(null);
export const useMicRecorder = () => useContext(MicRecorderContext);
